import React, { useState } from 'react';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Box
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { COLORS } from 'components';

interface CollapsibleDropdownProps {
    title: string;
    children: React.ReactNode;
    isExpanded?: boolean;
    onToggle?: (expanded: boolean) => void;
    showCount?: boolean;
    count?: number;
    sx?: any;
    titleSx?: any;
    contentSx?: any;
    accordionSx?: any;
}

export const CollapsibleDropdown = ({
    title,
    children,
    isExpanded: controlledExpanded,
    onToggle,
    showCount = false,
    count = 0,
    sx,
    titleSx,
    contentSx,
    accordionSx
}: CollapsibleDropdownProps) => {
    const [internalExpanded, setInternalExpanded] = useState(false);

    // Use controlled state if provided, otherwise use internal state
    const expanded = controlledExpanded !== undefined ? controlledExpanded : internalExpanded;

    const handleToggle = (event: React.SyntheticEvent, isExpanded: boolean) => {
        if (onToggle) {
            onToggle(isExpanded);
        } else {
            setInternalExpanded(isExpanded);
        }
    };

    const displayTitle = showCount ? `${title} (${count})` : title;

    return (
        <Box sx={sx}>
            <Accordion
                expanded={expanded}
                onChange={handleToggle}
                sx={{
                    boxShadow: 'none',
                    backgroundColor: 'transparent',
                    '&:before': { display: 'none' },
                    border: '1px solid #D9D9D9',
                    borderTop: 'none',
                    borderTopLeftRadius: "0px !important",
                    borderTopRightRadius: "0px !important",
                    borderBottomLeftRadius: '10px !important',
                    borderBottomRightRadius: '10px !important',
                    ...accordionSx
                }}
                disableGutters
            >
                <AccordionSummary
                    sx={{
                        backgroundColor: titleSx?.backgroundColor || '#F8FAFC',
                        minHeight: '63px',
                        borderBottom: expanded ? '1px solid #C0D0D4' : 'none',

                        '& .MuiAccordionSummary-content': {
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%',
                            margin: 0,
                        },
                        ...titleSx
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ExpandMoreIcon
                            sx={{
                                color: COLORS.Mainblue,
                                fontSize: '1.5rem',
                                transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                                transition: 'transform 0.3s ease',
                            }}
                        />
                        <Typography
                            variant="body1"
                            sx={{
                                fontWeight: 600,
                                fontSize: '14px',
                                color: COLORS.Mainblue,
                                ...titleSx
                            }}
                        >
                            {displayTitle}
                        </Typography>
                    </Box>
                </AccordionSummary>

                <AccordionDetails sx={{
                    p: 0,
                    backgroundColor: contentSx?.backgroundColor || 'white',
                    ...contentSx
                }}>
                    {children}
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};
